import { createContext, useContext } from "react";

// Textos de la interfaz: los mismos de platform.ui_label que usa el Platform Admin actual, en el idioma de la cookie de
// ayuda bheli-locale. Una clave que falta se ve como ⟦CLAVE⟧ (nunca en blanco).

export type Locale = "es-PE" | "en-US";
export type Label = (key: string) => string;

export function localeFromCookie(cookie: string = document.cookie): Locale {
  const value = cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith("bheli-locale="))
    ?.slice("bheli-locale=".length);
  return value === "en-US" ? "en-US" : "es-PE";
}

/** Prefijos de todas las claves que usa el Platform Admin nuevo (un solo pedido al arrancar). */
export const textPrefixes = [
  "NPA_",
  "ADM_",
  "PLATFORM_ROLE_",
  "NAV_ACCOUNT",
  "LOGIN_",
  "PWD_",
  "ORG",
  "ADD_",
  "USERS_",
  "ACCOUNT_",
  "COMPANY_COMMERCIAL_NAME",
  "FEEDBACK_",
  "ADMIN_ERR_",
  "ACTION_CANCEL",
  "ROLE_AGENT",
  "ROLE_SUPERVISOR",
  "REG_FIRST",
  "REG_LAST",
  "INVITE_EMAIL",
  "INVITE_ROLE",
  "CFG1_R1_OPTIONAL",
  // «¿Descartar los cambios?» de una ventana con cambios sin guardar (Cambiar precio, C12).
  "CFG1_R1_DISCARD",
  "CFG1_B_MODULE_",
  "CFG1_SEARCH",
  "CFG1_R1_CLEAR_SEARCH",
  "COUNTRY_",
  "LANG_",
  "CUR_",
  // Inteligencia artificial: las claves del Platform Admin actual.
  "AI_",
  "NAV_",
  "CONFIG_",
  "ACTION_",
  "ROUTE_",
  "PROVIDER",
  "PROBLEM_",
  "CONNECT",
  "CONN_",
  "ADDKEY_",
  "MODEL",
  "CHAIN_",
  "ROUTING_",
  "ADV_",
  "MANUAL_",
  "CUSTOM_",
  "USAGE_",
  "TRACE_",
  "EXEC_",
  "OPTYPE_",
  "ORGMODS_",
  "MSHIP_",
  // Empresas y Personas (R3): estados de invitación, eventos de seguridad y soporte delegado.
  "INV_STATUS_",
  "EVT_",
  "SUPPORT_",
  // Empleados IA y conocimiento (R5): textos de hoy que se reutilizan del Platform Admin actual.
  "APR_",
  // Mensajería (R6): simulador, tipo de canal y canal de simulación del Platform Admin actual.
  "SIM_",
  "CHTYPE_",
  "CH_SIM",
  // Mi cuenta (R7): textos de cuenta del Platform Admin actual (Error Hub usa solo claves ADM_HUB_*).
  "SEC_",
  "MFA_",
  "THEME_",
  "PREFERENCES_",
  "PROFILE_",
  "PREF_",
  // Orden de una tabla en el celular (C28): los mismos textos de Contactos del Company Portal.
  "C1_SORT_BY",
  "C1_ORDER_",
];

export async function loadTexts(locale: Locale): Promise<Record<string, string>> {
  const response = await fetch(`/api/admin/texts/${locale}?prefixes=${encodeURIComponent(textPrefixes.join(","))}`, {
    credentials: "same-origin",
  });
  return response.ok ? ((await response.json()) as Record<string, string>) : {};
}

export const missingText = (key: string) => `⟦${key}⟧`;

/** Reemplaza {0}, {1}… como string.Format. */
export function formatText(template: string, ...values: (string | number)[]): string {
  return template.replace(/\{(\d+)\}/g, (match, index: string) => {
    const value = values[Number(index)];
    return value === undefined ? match : String(value);
  });
}

export type Texts = { L: Label; locale: Locale };

export const TextsContext = createContext<Texts>({ L: missingText, locale: "es-PE" });

export const useTexts = () => useContext(TextsContext);
